import { Alert, Spin, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import * as XLSX from 'exceljs'
import Papa from 'papaparse'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const ViewerContainer = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 12.5rem;
`

const ErrorContainer = styled.div`
  padding: 1rem;
  color: ${CantileverTheme.colors.error[500]};
`

const TableContainer = styled.div`
  .ant-table-thead > tr > th {
    background-color: ${({ theme }) => theme.forecast.background2};
    color: ${({ theme }) => theme.forecast.defaultText};
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    font-size: 0.875rem;
  }
`

interface ExcelCsvViewerProps {
  url: string
  fileType: string
  className?: string
}

interface TableData {
  key: string
  [key: string]: string | number | null
}

export const ExcelCsvViewer = ({
  url,
  fileType,
  className,
}: ExcelCsvViewerProps) => {
  const [data, setData] = useState<TableData[]>([])
  const [columns, setColumns] = useState<ColumnsType<TableData>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const parseExcelFile = useCallback(async (fileUrl: string) => {
    try {
      const response = await fetch(fileUrl)
      const arrayBuffer = await response.arrayBuffer()

      const workbook = new XLSX.Workbook()
      await workbook.xlsx.load(arrayBuffer)

      // Get the first worksheet
      const worksheet = workbook.getWorksheet(1)
      if (!worksheet) {
        throw new Error('No worksheets found')
      }

      const rows: TableData[] = []
      const headerRow: string[] = []

      // Get headers from first row
      worksheet.getRow(1).eachCell((cell, colNumber) => {
        headerRow[colNumber - 1] =
          cell.value?.toString() || `Column ${colNumber}`
      })

      // Process data rows (limit to 1000 rows)
      const maxRows = Math.min(worksheet.rowCount, 1000)
      for (let rowNumber = 2; rowNumber <= maxRows; rowNumber++) {
        const row = worksheet.getRow(rowNumber)
        const rowData: TableData = { key: `row-${rowNumber}` }

        headerRow.forEach((header, index) => {
          const cell = row.getCell(index + 1)
          rowData[header] = cell.value?.toString() || null
        })

        rows.push(rowData)
      }

      // Create columns for antd table
      const tableColumns: ColumnsType<TableData> = headerRow.map((header) => ({
        title: header,
        dataIndex: header,
        key: header,
        width: 150,
        ellipsis: true,
      }))

      setColumns(tableColumns)
      setData(rows)
    } catch (err) {
      setError(
        `Error parsing Excel file: ${err instanceof Error ? err.message : 'Unknown error'}`
      )
    }
  }, [])

  const parseCsvFile = useCallback(async (fileUrl: string) => {
    try {
      const response = await fetch(fileUrl)
      const text = await response.text()

      Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError(
              `CSV parsing errors: ${results.errors.map((e) => e.message).join(', ')}`
            )
            return
          }

          const rows = results.data.slice(0, 1000).map((row, index) => ({
            key: `row-${index}`,
            ...row,
          })) as TableData[]

          // Create columns from headers
          const headers = Object.keys(results.data[0] || {})
          const tableColumns: ColumnsType<TableData> = headers.map(
            (header) => ({
              title: header,
              dataIndex: header,
              key: header,
              width: 150,
              ellipsis: true,
            })
          )

          setColumns(tableColumns)
          setData(rows)
        },
        error: (error: Error) => {
          setError(`CSV parsing error: ${error.message}`)
        },
      })
    } catch (err) {
      setError(
        `Error loading CSV file: ${err instanceof Error ? err.message : 'Unknown error'}`
      )
    }
  }, [])

  useEffect(() => {
    const loadFile = async () => {
      setLoading(true)
      setError(null)

      try {
        if (fileType === 'csv' || fileType === 'tsv') {
          await parseCsvFile(url)
        } else if (fileType === 'xlsx' || fileType === 'xls') {
          await parseExcelFile(url)
        } else {
          setError(`Unsupported file type: ${fileType}`)
        }
      } catch (err) {
        setError(
          `Error loading file: ${err instanceof Error ? err.message : 'Unknown error'}`
        )
      } finally {
        setLoading(false)
      }
    }

    loadFile()
  }, [url, fileType, parseExcelFile, parseCsvFile])

  if (loading) {
    return (
      <ViewerContainer className={className}>
        <LoadingContainer>
          <Spin size='large' tip='Loading spreadsheet data...' />
        </LoadingContainer>
      </ViewerContainer>
    )
  }

  if (error) {
    return (
      <ViewerContainer className={className}>
        <ErrorContainer>
          <Alert
            message='Error Loading File'
            description={error}
            type='error'
            showIcon
          />
        </ErrorContainer>
      </ViewerContainer>
    )
  }

  return (
    <ViewerContainer className={className}>
      <TableContainer>
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} rows`,
          }}
          scroll={{ x: 'max-content', y: 400 }}
          size='small'
          bordered
        />
      </TableContainer>
    </ViewerContainer>
  )
}
